import { registerComponent, executeScripts } from "helpers";
import WYATTElement from "wyatt";
import { checkForDuplicateIDs } from "dev-tools";

/**
 * Capture enough info to restore focus after this live-region is replaced.
 * @param {HTMLElement} root
 * @returns {{ type: "id", value: string }|{ type: "line-control", lineIndex: string, name: string|null }|null}
 */
function captureFocusTarget(root) {
  const active = document.activeElement;

  if (!(active instanceof HTMLElement) || !root.contains(active)) {
    return null;
  }

  if (active.id) {
    return { type: "id", value: active.id };
  }

  const lineIndex =
    active.dataset.lineItemIndex ??
    active.closest("quantity-stepper")?.querySelector("[data-line-item-index]")
      ?.dataset.lineItemIndex;

  if (!lineIndex) {
    return null;
  }

  return {
    type: "line-control",
    lineIndex,
    name: active.getAttribute("name"),
  };
}

/**
 * @param {{ type: "id", value: string }|{ type: "line-control", lineIndex: string, name: string|null }|null} target
 * @param {ParentNode} root
 */
function restoreFocus(target, root) {
  if (!target) {
    return;
  }

  let element = null;

  if (target.type === "id") {
    element = [...root.querySelectorAll("[id]")].find(
      (node) => node.id === target.value,
    );
  } else {
    const input = [...root.querySelectorAll("[data-line-item-index]")].find(
      (node) => node.dataset.lineItemIndex === target.lineIndex,
    );

    if (!input) {
      return;
    }

    if (target.name) {
      element = [
        ...(input.closest("quantity-stepper")?.querySelectorAll("[name]") ??
          []),
      ].find((node) => node.getAttribute("name") === target.name);
    } else {
      element = input;
    }
  }

  if (!(element instanceof HTMLElement)) {
    return;
  }

  element.focus({ preventScroll: true });
}

registerComponent(
  "live-region",
  class LiveRegion extends WYATTElement {
    constructor() {
      super();

      this.onUpdateDOM = this.conditionallyUpdateDOM.bind(this);

      if (!this.ariaLive) {
        this.ariaLive = "polite";
      }

      this.moveStyles();
    }

    connectedCallback() {
      this.subscribe(WYATT.PubSub.events.updateDOM, this.onUpdateDOM);
    }

    conditionallyUpdateDOM(data) {
      if (!data) {
        return;
      }

      const item = data.find(
        ({ liveRegion }) => liveRegion === this.dataset.liveRegion,
      );

      if (item?.html) {
        const newDOM = new DOMParser()
          .parseFromString(item.html, "text/html")
          .querySelector(`[data-live-region="${this.dataset.liveRegion}"]`);

        if (!newDOM) {
          return;
        }

        const focusTarget = captureFocusTarget(this);

        executeScripts(newDOM);

        this.moveStyles(newDOM);
        this.replaceWith(newDOM);

        checkForDuplicateIDs();
        restoreFocus(focusTarget, newDOM);
      }
    }

    moveStyles(DOM = this) {
      const assets = DOM.querySelectorAll(
        'link[rel="stylesheet"], [data-source]',
      );

      assets.forEach((asset) => {
        const fileName = this.fileName(asset);

        if (!fileName) {
          return;
        }

        if (WYATT.config.liveRegion.existingExternalAssets.includes(fileName)) {
          asset.remove();
        } else {
          document.head.appendChild(asset);
          WYATT.config.liveRegion.existingExternalAssets.push(fileName);
        }
      });
    }

    fileName(asset) {
      const source = asset.dataset.source || asset.src || asset.href;
      const assetMatch = source.match(/\/assets\/(.*?)\?/);

      return assetMatch ? assetMatch[1] : null;
    }
  },
);
